import React, { useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useConfig } from "@/lib/useConfig";
import { ApiError, mediaUrl, submitLead, submitWebLead, type BusinessIcon, type BusinessSegment, type LeadInterest, type LeadSegment, type LeadVolume } from "@/lib/api";
import { getWebProfile, getWebSession } from "@/lib/webSession";
import { DI, PATH, WI } from "@/components/desktop/desktopUi";

const CITIES = ["Ташкент", "Самарканд", "Бухара", "Наманган", "Андижан", "Фергана", "Нукус", "Карши", "Термез", "Ургенч", "Другой"];
const SEGMENTS: LeadSegment[] = ["horeca", "home_office", "other"];
const INTERESTS: Array<{ key: LeadInterest; icon: string; t: string }> = [
  { key: "beans", icon: PATH.bean, t: "Beans" },
  { key: "equipment", icon: PATH.gear, t: "Equipment" },
  { key: "training", icon: PATH.people, t: "Training" },
  { key: "service", icon: '<path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5"/><path d="M14.5 6.5 17 4l3 3-2.5 2.5"/>', t: "Service" },
];
const VOLUMES: LeadVolume[] = ["до 5 кг", "5–20 кг", "20–50 кг", "50+ кг"];
const VOLUME_TICKS = ["≤ 5", "5–20", "20–50", "50+"];

const ICONS: Record<BusinessIcon, string> = {
  bean: PATH.bean,
  machine: PATH.gear,
  people: PATH.people,
  truck: PATH.truck,
  cup: '<path d="M4 9h12v4.5A5.5 5.5 0 0 1 10.5 19h-1A5.5 5.5 0 0 1 4 13.5z"/><path d="M16 10.5h1.5a2.5 2.5 0 0 1 0 5H16"/><path d="M8 3.5c-.7.9.7 1.6 0 2.5M12 3.5c-.7.9.7 1.6 0 2.5"/>',
  award: PATH.award,
  leaf: PATH.leaf,
  gear: '<path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5"/><path d="M14.5 6.5 17 4l3 3-2.5 2.5"/>',
};
type SegKey = "cafe" | "restaurant" | "office";
const SEG_KEYS: SegKey[] = ["cafe", "restaurant", "office"];
const DEFAULT_ICONS: Record<SegKey, BusinessIcon[]> = { cafe: ["bean", "people", "machine", "truck"], restaurant: ["cup", "bean", "people", "truck"], office: ["machine", "bean", "gear", "cup"] };
type L3 = { ru: string; uz: string; en: string } | undefined;

/** "Кофе, который *продаёт* ваши напитки" → the starred word in gold italics. */
function Accent({ text }: { text: string }) {
  const parts = text.split(/\*([^*]+)\*/);
  return <>{parts.map((p, i) => (i % 2 ? <em key={i}>{p}</em> : <React.Fragment key={i}>{p}</React.Fragment>))}</>;
}

/**
 * Desktop "Для бизнеса" page: a photo hero with a short step-by-step request form,
 * what each type of business gets (tabs), client logos and a closing call to action.
 * Every text and photo comes from the admin (Settings → «Для бизнеса»); empty fields
 * use the built-in texts. Leads land in the same funnel as before.
 */
export function DesktopLead() {
  const { t, locale } = useI18n();
  const { route, navigate } = useRouter();
  const { site, business: b, botUsername } = useConfig();
  const signedIn = !!getWebSession();
  const profile = signedIn ? getWebProfile() : null;
  const source = ((route.name === "lead" && route.source) || (route.name === "b2b" ? "b2b" : "site")).replace(/[^a-z0-9_-]/gi, "").toLowerCase().slice(0, 40) || "site";
  const pick = (v: L3, key: string) => v?.[locale]?.trim() || v?.ru?.trim() || t(key);

  // ---- form ----
  const formRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(1);
  const [segment, setSegment] = useState<LeadSegment>("horeca");
  const [city, setCity] = useState("Ташкент");
  const [interests, setInterests] = useState<LeadInterest[]>(["beans"]);
  const [volume, setVolume] = useState<LeadVolume | undefined>();
  const [fullName, setFullName] = useState(profile?.firstName ?? "");
  const [orgName, setOrgName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const needsOrg = segment === "horeca";
  const nameOk = fullName.trim().length >= 2;
  const phoneOk = phone.replace(/\D/g, "").length >= 9;
  const orgOk = !needsOrg || orgName.trim().length > 0;
  const valid = nameOk && phoneOk && orgOk && consent;
  const volIdx = volume ? VOLUMES.indexOf(volume) : -1;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      setShowErrors(true);
      return;
    }
    setBusy(true);
    setError(null);
    const base = {
      segment,
      fullName: fullName.trim(),
      city,
      orgName: orgName.trim() || undefined,
      phone: phone.trim(),
      interests,
      volume: segment === "other" ? undefined : volume,
      comment: comment.trim() || undefined,
      source,
    };
    try {
      if (signedIn) await submitLead({ ...base, canTelegram: true });
      else await submitWebLead({ ...base, consent: true, website });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  const toForm = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(() => formRef.current?.querySelector<HTMLElement>("button, input")?.focus({ preventScroll: true }), 500);
  };

  // ---- page content ----
  const heroImg = b?.heroImage || site?.b2bImage || "/brand/editorial.webp";
  const ticks = (b?.ticks ?? []).map((x) => pick(x, "")).filter(Boolean);
  const shownTicks = ticks.length ? ticks : [t("biz.tick1"), t("biz.tick2"), t("biz.tick3")];
  const segs = SEG_KEYS.map((key) => ({ key, s: (b?.segments?.[key] ?? {}) as BusinessSegment })).filter((x) => x.s.enabled !== false);
  const [tab, setTab] = useState<SegKey>("cafe");
  const current = segs.find((x) => x.key === tab) ?? segs[0];
  const cards = current
    ? current.s.cards?.length
      ? current.s.cards.map((c) => ({ icon: c.icon, title: pick(c.title, ""), text: pick(c.text, "") }))
      : [1, 2, 3, 4].map((i) => ({ icon: DEFAULT_ICONS[current.key][i - 1], title: t(`biz.seg.${current.key}.c${i}t`), text: t(`biz.seg.${current.key}.c${i}x`) }))
    : [];
  const logos = (b?.logos ?? []).filter((l) => l.image || l.name.trim());
  const chooseTab = (key: SegKey) => {
    setTab(key);
    if (step === 1 && !done) setSegment(key === "office" ? "home_office" : "horeca");
  };

  const progress = (
    <div className="bz-prog">
      {[1, 2, 3].map((n) => (
        <div key={n} data-state={n < step ? "done" : n === step ? "now" : "next"}>
          <i />
          <span>{n} · {t(`biz.step${n}`)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bz">
      <section className="bz-hero" style={{ backgroundImage: `url(${mediaUrl(heroImg)})`, backgroundPosition: `${site?.b2bImagePos ?? "center"} center` }}>
        <div className="bz-hero__in">
          <div className="bz-hero__text">
            <div className="dc-crumbs">
              <button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button> › <span>{t("profile.b2b")}</span>
            </div>
            <div className="bz-eyebrow">{pick(b?.heroEyebrow, "biz.heroEyebrow")}</div>
            <h1 className="dk-serif"><Accent text={pick(b?.heroTitle, "biz.heroTitle")} /></h1>
            <p>{pick(b?.heroText, "biz.heroText")}</p>
            <ul className="bz-ticks">
              {shownTicks.map((x, i) => (
                <li key={i}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  {x}
                </li>
              ))}
            </ul>
          </div>

          <div className="bz-form" ref={formRef}>
            {done ? (
              <div className="bz-done">
                <span className="bz-done__mark">✓</span>
                <h2 className="dk-serif">{t("lead.doneTitle")}</h2>
                <p>{signedIn ? t("lead.doneText") : t("desktop.leadDoneGuest")}</p>
                <button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("lead.toCatalog")} {DI.arrow}</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                {progress}
                {step === 1 && (
                  <div className="bz-step" key="s1">
                    <h2 className="dk-serif">{t("biz.s1Title")}</h2>
                    <p className="bz-sub">{t("biz.s1Sub")}</p>
                    <div className="bz-segs">
                      {SEGMENTS.map((s) => (
                        <button type="button" key={s} data-on={segment === s} onClick={() => setSegment(s)}>
                          <b>{t(`lead.seg.${s}`)}</b>
                          <small>{t(`lead.segHint.${s}`)}</small>
                        </button>
                      ))}
                    </div>
                    <div className="bz-field">
                      <label htmlFor="bz-city">{t("lead.city")}</label>
                      <select id="bz-city" value={city} onChange={(e) => setCity(e.target.value)}>
                        {CITIES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                )}
                {step === 2 && (
                  <div className="bz-step" key="s2">
                    <h2 className="dk-serif">{t("biz.s2Title")}</h2>
                    <p className="bz-sub">{t("biz.s2Sub")}</p>
                    <div className="bz-tiles">
                      {INTERESTS.map((i) => (
                        <button type="button" key={i.key} data-on={interests.includes(i.key)} aria-pressed={interests.includes(i.key)} onClick={() => setInterests((p) => (p.includes(i.key) ? p.filter((x) => x !== i.key) : [...p, i.key]))}>
                          <span className="bz-tiles__ic">{WI(i.icon, 40)}</span>
                          <span><b>{t(`biz.int${i.t}`)}</b><small>{t(`biz.int${i.t}S`)}</small></span>
                        </button>
                      ))}
                    </div>
                    {segment !== "other" && (
                      <div className="bz-vol">
                        <div className="bz-vol__head">
                          <span>{t("biz.volLabel")}</span>
                          <b>{volume === "не знаю" ? t("biz.volUnknown") : volIdx >= 0 ? `${VOLUME_TICKS[volIdx]} ${t("biz.kg")}` : t("biz.volPick")}</b>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={3}
                          step={1}
                          value={volIdx >= 0 ? volIdx : 1}
                          data-empty={volIdx < 0}
                          aria-label={t("biz.volLabel")}
                          style={{ ["--p" as string]: `${((volIdx >= 0 ? volIdx : 1) / 3) * 100}%` }}
                          onChange={(e) => setVolume(VOLUMES[Number(e.target.value)])}
                          onClick={(e) => setVolume(VOLUMES[Number((e.target as HTMLInputElement).value)])}
                        />
                        <div className="bz-vol__ticks">
                          {VOLUME_TICKS.map((x, i) => (
                            <button type="button" key={x} data-on={volIdx === i} onClick={() => setVolume(VOLUMES[i])}>{x}</button>
                          ))}
                        </div>
                        <div className="bz-vol__foot">
                          <button type="button" data-on={volume === "не знаю"} onClick={() => setVolume(volume === "не знаю" ? undefined : "не знаю")}>{t("biz.volUnknown")}</button>
                          {volIdx >= 1 && <span className="bz-vol__hint">✓ {t("biz.wholesale")}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {step === 3 && (
                  <div className="bz-step" key="s3">
                    <h2 className="dk-serif">{t("biz.s3Title")}</h2>
                    <p className="bz-sub">{t("biz.s3Sub")}</p>
                    <div className="bz-2">
                      <div className="bz-field">
                        <label htmlFor="bz-name">{t("lead.fullName")}</label>
                        <input id="bz-name" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} aria-invalid={showErrors && !nameOk} />
                      </div>
                      <div className="bz-field">
                        <label htmlFor="bz-org">{segment === "horeca" ? t("lead.orgHoreca") : segment === "home_office" ? t("lead.orgOffice") : t("lead.orgOther")}</label>
                        <input id="bz-org" autoComplete="organization" value={orgName} onChange={(e) => setOrgName(e.target.value)} aria-invalid={showErrors && !orgOk} />
                      </div>
                    </div>
                    <div className="bz-field">
                      <label htmlFor="bz-phone">{t("desktop.leadPhone")}</label>
                      <input id="bz-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+998 90 123 45 67" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s()-]/g, "").slice(0, 20))} aria-invalid={showErrors && !phoneOk} />
                      {showErrors && !phoneOk && <span className="bz-err">{t("inquiry.phoneInvalid")}</span>}
                    </div>
                    <div className="bz-field">
                      <label htmlFor="bz-comment">{t("lead.comment")}</label>
                      <textarea id="bz-comment" rows={2} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
                    </div>
                    <input className="dl-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} />
                    <label className={`bz-consent ${showErrors && !consent ? "bz-consent--error" : ""}`}>
                      <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                      <span>
                        {t("biz.consent")} <button type="button" onClick={() => navigate({ name: "post", slug: "privacy" })}>{t("biz.privacy")}</button>
                      </span>
                    </label>
                    {error && <div className="dl-banner">{error}</div>}
                  </div>
                )}
                <div className="bz-nav">
                  {step > 1 ? <button type="button" className="bz-back" onClick={() => setStep(step - 1)}>← {t("biz.back")}</button> : <span />}
                  {step < 3 ? (
                    <button type="button" className="dk-btn bz-next" onClick={() => setStep(step + 1)}>{t("biz.next")} {DI.arrow}</button>
                  ) : (
                    <button type="submit" className="dk-btn bz-next" disabled={busy}>{busy ? "…" : t("lead.submit")} {!busy && DI.arrow}</button>
                  )}
                </div>
                <div className="bz-alt">
                  {t("biz.alt")}{" "}
                  {botUsername && <><a href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">Telegram</a> {t("biz.altOr")}{" "}</>}
                  <button type="button" onClick={() => window.dispatchEvent(new Event("kms:chat"))}>{t("biz.altChat")}</button>
                </div>
                {!signedIn && step === 3 && <p className="bz-note">{t("desktop.leadGuestNote")}</p>}
              </form>
            )}
          </div>
        </div>
      </section>

      {b?.showSegments !== false && current && (
        <section className="bz-seg">
          <div className="bz-seg__head">
            <div>
              <div className="dk-eyebrow">{t("biz.segEyebrow")}</div>
              <h2 className="dk-serif">{t("biz.segTitle")}</h2>
            </div>
            {segs.length > 1 && (
              <div className="bz-tabs" role="tablist">
                {segs.map(({ key, s }) => (
                  <button key={key} role="tab" aria-selected={current.key === key} data-on={current.key === key} onClick={() => chooseTab(key)}>
                    {pick(s.label, `biz.seg.${key}.label`)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="bz-grid" key={current.key}>
            <div className="bz-big" style={{ backgroundImage: `url(${mediaUrl(current.s.image || heroImg)})` }}>
              <div className="bz-eyebrow">{pick(current.s.label, `biz.seg.${current.key}.label`)}</div>
              <h3 className="dk-serif"><Accent text={pick(current.s.title, `biz.seg.${current.key}.title`)} /></h3>
              <p>{pick(current.s.text, `biz.seg.${current.key}.text`)}</p>
            </div>
            {cards.map((c, i) => (
              <div key={i} className="bz-card">
                {WI(ICONS[c.icon] ?? PATH.bean, 50)}
                <b>{c.title}</b>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {b?.showLogos !== false && logos.length > 0 && (
        <section className="bz-logos">
          <div className="dk-eyebrow">{t("biz.logosEyebrow")}</div>
          <h2 className="dk-serif">{pick(b?.logosTitle, "biz.logosTitle")}</h2>
          <div className="bz-logos__row">
            {logos.map((l, i) => {
              const inner = l.image ? <img src={mediaUrl(l.image, 400)} alt={l.name} loading="lazy" /> : <span>{l.name}</span>;
              return l.url ? (
                <a key={i} href={l.url} target="_blank" rel="noreferrer" title={l.name}>{inner}</a>
              ) : (
                <div key={i} title={l.name}>{inner}</div>
              );
            })}
          </div>
        </section>
      )}

      {b?.showCta !== false && (
        <section className="bz-cta">
          <div>
            <h2 className="dk-serif">{pick(b?.ctaTitle, "biz.ctaTitle")}</h2>
            <p>{pick(b?.ctaText, "biz.ctaText")}</p>
          </div>
          <div className="bz-cta__btns">
            <button className="bz-cta__gold" onClick={toForm}>{t("biz.ctaForm")} ↑</button>
            {botUsername ? (
              <a className="bz-cta__line" href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">{t("biz.ctaTg")}</a>
            ) : (
              <button className="bz-cta__line" onClick={() => window.dispatchEvent(new Event("kms:chat"))}>{t("desktop.chatFab")}</button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
